import * as accounting from "accounting";
import moment from "moment";
import InvestmentState from "types/InvestmentState";
import XpectoComplianceStatus from "types/XpectoComplianceStatus";
import { CouponType } from "types/CouponType";

export function formatMoney(cents: number | string | undefined, precision?: number): string {
  if (cents == null) return format(0, "€");
  const euros = Number(`${cents.toString()}e-2`);
  return format(euros, "€", 1, precision);
}

export function formatPercentage(value: number | string | undefined, multiplicationFactor: number = 100): string {
  return format(value, "%", multiplicationFactor);
}

export function formatNumber(value: number | string | undefined): string {
  return format(value, "");
}

export function formatUuid(uuid: string): string {
  return uuid?.substring(0, 8);
}

function format(value: number | string | undefined, symbol: string, multiplicationFactor: number = 1, precision: number = 2): string {
  const config: accounting.CurrencySettings<"%v %s"> = {
    symbol,
    format: "%v %s",
    decimal: ",",
    thousand: ".",
    precision
  };
  if (value == null) return accounting.formatMoney(0, config);
  const parsedValue = typeof value === "string" ? parseFloat(value) : value;
  return accounting.formatMoney(parsedValue * multiplicationFactor, config);
}

export function parseNumber(value: string | undefined): number {
  if (value == null) return 0;
  const cleanedValue = value.replace(/[^\d,]/g, "").replace(/[,]/g, ".");
  if (cleanedValue.match(/^[\d]+.[\d]+$/g) == null) return 0;
  return parseFloat(cleanedValue);
}

export function formatInvestmentState(state: InvestmentState): string {
  switch (state) {
    case InvestmentState.created:
      return "Erstellt";
    case InvestmentState.investorInformationEmailApproved:
      return "Investoreninformations-Email bestätigt";
    case InvestmentState.contractDataApproved:
      return "Vertragsdaten bestätigt";
    case InvestmentState.investorSurveyApproved:
      return "Angemessenheitsprüfung bestätigt";
    case InvestmentState.submitted:
      return "Eingereicht / Abgeschlossen";
    case InvestmentState.revocationFree:
      return "Widerrufsfrei";
    case InvestmentState.submittedSigningDeclaration:
      return "Zeichnungserklärung abgegeben";
    case InvestmentState.inProgressByEffecta:
      return "In Bearbeitung bei Effecta";
    case InvestmentState.acceptedByEffecta:
      return "Akzeptiert von Effecta";
    case InvestmentState.declinedByEffecta:
      return "Abgelehnt von Effecta";
    case InvestmentState.acceptedByEmitter:
      return "Akzeptiert vom Emittenten";
    case InvestmentState.declinedByEmitter:
      return "Abgelehnt vom Emittenten";
    case InvestmentState.paid:
      return "Bezahlt";
    case InvestmentState.paidAndRevocationFree:
      return "Bezahlt und Widerrufsfrei";
    case InvestmentState.active:
      return "Aktiv";
    case InvestmentState.repaid:
      return "Zurückgezahlt";
    case InvestmentState.cancelled:
      return "Storniert";
    case InvestmentState.insolvent:
      return "Insolvent";
    case InvestmentState.aborted:
      return "Abgebrochen";
    default:
      return state;
  }
}

export function formatXpectoComplianceStatus(status: XpectoComplianceStatus): string {
  switch (status) {
    case "0":
      return "unknown";
    case "1":
      return "approval";
    case "2":
      return "approvalwithinappropiatenessdeclaration";
    case "3":
      return "approvalcaseofinassessability";
    case "100":
      return "resubmission";
    case "101":
      return "resubmissioninappropiateness";
    case "102":
      return "resubmissionmissingdocuments";
    case "200":
      return "failure";
    default:
      return status;
  }
}

export function formatDate(dateTimeISO?: string, useGermanFormat?: boolean): string {
  if (dateTimeISO == null) return "-";
  return moment(dateTimeISO).format(useGermanFormat === true ? "DD.MM.YYYY" : "YYYY-MM-DD");
}

export function formatDateTime(dateTimeISO?: string, useGermanFormat?: boolean): string {
  if (dateTimeISO == null) return "-";
  return moment(dateTimeISO).format(useGermanFormat === true ? "DD.MM.YYYY HH:mm:ss" : "YYYY-MM-DD HH:mm:ss Z");
}

export function formatCouponType(type?: CouponType): string {
  if (type == null) return "-";
  switch (type) {
    case CouponType.amountInCt:
      return "Gutschein mit Festbetrag";
    case CouponType.percentage:
      return "Prozentualler Gutschein";
    default:
      return "-";
  }
}

export function formatError(errors: any): JSX.Element | string {
  console.log(JSON.stringify(errors));
  const errorMessage: string | undefined =
    errors.graphQLErrors != null && errors.graphQLErrors.length > 0 ? errors.graphQLErrors[0].message : undefined;
  if (errorMessage != null) return errorMessage;
  return "Unbekannter Fehler";
}
